import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import traderbroLogo from "../assets/traderbro-logo.svg";
import { GOOGLE_CLIENT_ID } from "../config";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/chat";
  const [errors, setErrors] = useState({});
  const [googleLoading, setGoogleLoading] = useState(false);
  const { loginWithGoogle, isAuthenticated } = useAuth();
  const googleButtonRef = useRef(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/chat", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Google Sign-In handler
  const handleGoogleSignIn = useCallback(
    async (response) => {
      setGoogleLoading(true);
      setErrors({});

      console.log("handel Google Login Response ", response);

      try {
        const success = await loginWithGoogle(response.credential);
        if (success) {
          navigate(from, { replace: true });
        } else {
          setErrors({ general: "Google sign in failed. Please try again." });
        }
      } catch (error) {
        setErrors({
          general: error.message || "An error occurred during Google sign in.",
        });
      } finally {
        setGoogleLoading(false);
      }
    },
    [loginWithGoogle, from, navigate]
  );

  // Initialize Google Sign-In
  useEffect(() => {
    let checkInterval = null;

    const initializeGoogleSignIn = () => {
      if (window.google && googleButtonRef.current) {
        if (googleButtonRef.current.firstChild) {
          googleButtonRef.current.innerHTML = "";
        }

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleSignIn,
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: "outline",
          size: "large",
          width: "100%",
          text: "signin_with",
        });
      }
    };

    if (window.google) {
      initializeGoogleSignIn();
    } else {
      checkInterval = setInterval(() => {
        if (window.google) {
          initializeGoogleSignIn();
          clearInterval(checkInterval);
        }
      }, 100);

      setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
      }, 10000);
    }

    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = "";
      }
    };
  }, [handleGoogleSignIn]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute w-[600px] h-[600px] bg-gradient-to-br from-brand-cta/8 via-emerald-500/5 to-transparent rounded-full blur-3xl -top-48 -right-48 animate-float"></div>
        <div className="absolute w-[500px] h-[500px] bg-gradient-to-tl from-brand-cta/6 via-teal-500/4 to-transparent rounded-full blur-3xl top-1/2 -left-32 animate-float-delayed"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img src={traderbroLogo} alt="TraderBro" className="h-12 w-auto" />
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-brand-dark mb-2">
            Sign in to your account
          </h2>
          <p className="text-gray-600">Welcome back! Sign in to continue</p>
        </div>

        {/* Form Card */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50">
          {errors.general && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          <div className="space-y-6">
            {/* Google Sign-In Button */}
            <div>
              <div
                ref={googleButtonRef}
                className="w-full flex justify-center"
                style={{ minHeight: "40px" }}
              ></div>
              {googleLoading && (
                <div className="mt-2 text-center text-sm text-gray-600">
                  Signing in with Google...
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
